import React, { useEffect, useState } from 'react';
import { IonChip, IonItem, IonLabel, IonList, IonNote, IonSegment, IonSegmentButton, IonSpinner } from '@ionic/react';
import { ServiceEstimate } from '../../../interfaces/ServiceEstimate';
import ConfirmDeleteBtnModal from '../Modals/ConfirmDeleteBtnModal';
import { serviceApi } from '../../../utils/serviceEstimatesApi';
import { STATUS_COLOR, STATUS_LABEL, money, shortDate } from './serviceFormat';
import './SavedEstimates.css';

interface Props {
  onOpen: (e: ServiceEstimate) => void;
  onDeleted: (id: string) => void;
  refreshKey: number;
}

const SavedEstimates: React.FC<Props> = ({ onOpen, onDeleted, refreshKey }) => {
  const [scope, setScope] = useState<'mine' | 'team'>('mine');
  const [rows, setRows] = useState<ServiceEstimate[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setRows(null);
    serviceApi.listEstimates(scope).then(setRows).catch((e) => { setError(e instanceof Error ? e.message : 'Could not load estimates.'); setRows([]); });
  }, [scope, refreshKey]);

  /** Same rule as the API: only a draft that never got an ACS# can be deleted. */
  const isDeletable = (e: ServiceEstimate) => e.status === 'draft' && !e.acs_number;

  const remove = async (id: string) => {
    setError('');
    try {
      await serviceApi.deleteDraft(id);
      setRows((r) => (r ? r.filter((x) => x.id !== id) : r));
      onDeleted(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the estimate.');
    }
  };

  return (
    <div className="saved-estimates">
      <IonSegment value={scope} onIonChange={(e) => setScope(e.detail.value as 'mine' | 'team')}>
        <IonSegmentButton value="mine"><IonLabel>My estimates</IonLabel></IonSegmentButton>
        <IonSegmentButton value="team"><IonLabel>Team</IonLabel></IonSegmentButton>
      </IonSegment>
      {rows === null && <IonSpinner />}
      {error && <p className="saved-error">{error}</p>}
      {rows && rows.length === 0 && !error && <p className="saved-empty">No estimates yet. Start one on the Estimate tab.</p>}
      {rows && rows.length > 0 && (
        <IonList inset>
          {rows.map((e) => {
            const key = e.is_expired ? 'expired' : e.status;
            return (
              <IonItem key={e.id} button detail onClick={() => onOpen(e)}>
                <IonLabel>
                  <h3>{e.customer_detail.name}</h3>
                  <p>{shortDate(e.estimate_date)} · {e.prepared_by_name}{e.acs_number ? ` · ${e.acs_number}` : ''}</p>
                </IonLabel>
                {e.review_reasons?.length > 0 && e.status !== 'accepted' && e.status !== 'declined' && (
                  <IonChip color="warning" outline slot="end">Needs review</IonChip>
                )}
                <IonChip color={STATUS_COLOR[key]} slot="end">{STATUS_LABEL[key]}</IonChip>
                <IonNote slot="end" className="saved-total">{money(e.total_price)}</IonNote>
                {isDeletable(e) && (
                  // Stop the click reaching the row, which would open the estimate.
                  <span slot="end" onClick={(ev) => ev.stopPropagation()}>
                    <ConfirmDeleteBtnModal id={e.id} handleDelete={remove}>
                      Delete the draft estimate for {e.customer_detail.name}? This cannot be undone.
                    </ConfirmDeleteBtnModal>
                  </span>
                )}
              </IonItem>
            );
          })}
        </IonList>
      )}
    </div>
  );
};

export default SavedEstimates;
